import React, { useState, useEffect, useCallback, useMemo } from "react";
import Select from "react-select";
import { FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../index.css";
import api from "../../utils/api";
import log from "../../utils/logger"; // Import the logger

const ConfirmationModal = React.memo(({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full bg-gray-800 bg-opacity-50">
      <div className="relative p-4 w-full max-w-md max-h-full">
        <div className="relative bg-white squared-lg shadow dark:bg-gray-100">
          <button
            type="button"
            className="absolute top-3 right-2.5 text-white bg-primary hover:bg-gray-200 hover:text-gray-900 squared-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-red-800 dark:hover:text-white"
            onClick={onClose}
          >
            <FaTimes className="w-3 h-3" />
            <span className="sr-only">Close modal</span>
          </button>
          <div className="p-4 md:p-5 text-center">
            <FaTimes className="mx-auto mb-4 text-gray-400 w-12 h-12 dark:text-gray-600" />
            <h3 className="mb-5 text-lg font-normal text-gray-800 dark:text-gray-800">
              Are you sure you want to delete this card?
            </h3>
            <button
              onClick={onConfirm}
              type="button"
              className="text-white bg-primary hover:bg-primary-dark focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium squared-lg text-sm inline-flex items-center px-5 py-2.5 text-center"
            >
              Yes, I'm sure
            </button>
            <button
              onClick={onClose}
              type="button"
              className="py-2.5 px-5 ml-3 text-sm font-medium text-gray-600 focus:outline-none bg-white squared-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-100 dark:text-gray-600 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-600"
            >
              No, cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const CardBank = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState({});
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);

  const fetchCards = useCallback(async () => {
    try {
      const response = await api.get("/card/get-cards-for-admin");
      if (response.data.code === 200) {
        setCards(response.data.data);
      } else {
        toast.error("Failed to fetch cards");
        log.error(`Failed to fetch cards: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("Failed to fetch cards");
      log.error("Failed to fetch cards:", error);
    }
  }, []);

  const fetchCategoriesAndTags = useCallback(async () => {
    try {
      const [categoriesResponse, tagsResponse] = await Promise.all([
        api.get("/category/get-active-categories"),
        api.get("/tag/get-active-tags"),
      ]);

      if (categoriesResponse.data.code === 200) {
        setCategories(categoriesResponse.data.data);
      } else {
        toast.error("Failed to fetch categories");
        log.error(
          `Failed to fetch categories: ${categoriesResponse.data.message}`
        );
      }

      if (tagsResponse.data.code === 200) {
        setTags(tagsResponse.data.data);
      } else {
        toast.error("Failed to fetch tags");
        log.error(`Failed to fetch tags: ${tagsResponse.data.message}`);
      }
    } catch (error) {
      toast.error("Failed to fetch categories and tags");
      log.error("Failed to fetch categories and tags:", error);
    }
  }, []);

  useEffect(() => {
    fetchCards();
    fetchCategoriesAndTags();
  }, [fetchCards, fetchCategoriesAndTags]);

  const handleDeleteCard = async () => {
    try {
      const response = await api.put(`/card/delete-card/${cardToDelete}`);
      if (response.data.code === 200) {
        setCards((prev) => prev.filter((card) => card._id !== cardToDelete));
        toast.success("Card deleted successfully!");
      } else {
        toast.error("Failed to delete card");
        log.error(`Failed to delete card: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("Failed to delete card");
      log.error("Failed to delete card:", error);
    } finally {
      setModalIsOpen(false);
      setCardToDelete(null);
    }
  };

  const openModal = (id) => {
    setCardToDelete(id);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setCardToDelete(null);
  };

  const handleImageLoad = (id) => {
    setLoading((prev) => ({ ...prev, [id]: false }));
  };

  const handleImageError = (id) => {
    setLoading((prev) => ({ ...prev, [id]: false }));
  };

  const filteredCards = useMemo(() => {
    return cards.filter((card) => {
      if (
        selectedCategory &&
        !card.categories.some((category) => category === selectedCategory.value)
      )
        return false;
      if (selectedTags.length > 0) {
        const cardTagIds = card.tags.map((tag) => tag);
        const selectedTagIds = selectedTags.map((tag) => tag.value);
        return selectedTagIds.every((tagId) => cardTagIds.includes(tagId));
      }
      return true;
    });
  }, [cards, selectedCategory, selectedTags]);

  const customStyles = {
    control: (base) => ({
      ...base,
      padding: "5px",
      borderRadius: "0px",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#f86060",
      borderRadius: "0px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      padding: "5px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": {
        backgroundColor: "#f51f1f",
        color: "white",
      },
      borderRadius: "0px",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isFocused ? "#f51f1f" : isSelected ? "#f86060" : null,
      color: isFocused || isSelected ? "white" : "#f51f1f",
      ":active": {
        ...base[":active"],
        backgroundColor: isSelected ? "#f51f1f" : null,
      },
    }),
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white squared-lg shadow-lg relative z-0">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <div className="mb-4 flex md:flex-row flex-col gap-2 justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Card Bank</h2>
          <p className="text-sm text-gray-600">
            Showing {filteredCards.length} out of {cards.length} cards
          </p>
        </div>
        <div className="flex lg:flex-row flex-col gap-2 items-center">
          <div className="w-80">
            <Select
              placeholder="Select Occasion"
              options={categories.map((category) => ({
                value: category._id,
                label: category.name,
              }))}
              onChange={setSelectedCategory}
              isClearable
              styles={customStyles}
            />
          </div>
          <div className="w-80">
            <Select
              placeholder="For whom"
              options={tags.map((tag) => ({ value: tag._id, label: tag.name }))}
              onChange={setSelectedTags}
              isMulti
              styles={customStyles}
            />
          </div>
        </div>
      </div>
      {filteredCards.length === 0 && (
        <div className="text-center w-full min-h-[500px] flex flex-col justify-center items-center text-gray-500">
          <svg
            className="w-16 h-16 mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 14l2-2m0 0l2-2m-2 2v6m0-6l-2-2m2 2l2-2m-2 2l2-2M4 4l16 16"
            />
          </svg>
          <p className="text-lg font-semibold">No results found</p>
          {/* <p className="text-sm mt-2">Try adjusting your search or filters to find what you’re looking for.</p> */}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCards.map((card) => (
          <div
            key={card._id}
            className="relative bg-gray-100 squared-lg shadow-md w-full"
            style={{ paddingBottom: "140%", position: "relative", zIndex: 0 }}
          >
            <button
              type="button"
              onClick={() => openModal(card._id)}
              className="absolute top-0 left-0 bg-primary text-white squared-full p-1 m-2 z-10"
            >
              <FaTimes size={12} />
            </button>
            {loading[card._id] && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-200 squared-lg">
                <div className="loader"></div>
              </div>
            )}
            <img
              src={card.thumbnail}
              alt={card.title}
              className={`top-0 left-0 w-full h-[93%] object-cover squared-lg ${
                loading[card._id] ? "hidden" : "block"
              }`}
              onLoad={() => handleImageLoad(card._id)}
              onError={() => handleImageError(card._id)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "94%",
              }}
            />
            <span className="absolute bottom-0 left-2 text-base sm:bottom-0 sm:left-4 md:text-lg text-primary">
              Drafts: {card?.draftsCount || 0}
            </span>
            <span className="absolute bottom-0 right-2 text-base sm:bottom-0 sm:right-4 md:text-lg text-primary">
              Favorites: {card?.favoritesCount || 0}
            </span>
          </div>
        ))}
      </div>

      <ConfirmationModal
        isOpen={modalIsOpen}
        onClose={closeModal}
        onConfirm={handleDeleteCard}
      />
    </div>
  );
};

export default CardBank;
